/**
 * AdminRolesPanel — grant/revoke admin privileges from the GM Console.
 * Uses POST /api/admin/gm/users/set-admin. The endpoint is protected
 * by require_admin server-side and refuses to demote whitelist admins
 * or the current admin.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Shield, ShieldOff, Search, RefreshCw } from "lucide-react";

export default function AdminRolesPanel() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const refresh = async () => {
    try { setUsers(await api.adminUsers()); }
    catch (e) { toast.error("Errore nel caricamento utenti"); }
  };
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() =>
    (users?.users || []).filter(u =>
      !q ||
      (u.email || "").toLowerCase().includes(q.toLowerCase()) ||
      (u.character?.name || u.name || "").toLowerCase().includes(q.toLowerCase())
    ),
    [users, q]
  );

  const setRole = async (u, promote) => {
    const label = promote ? "concedere" : "revocare";
    const target = u.character?.name || u.email;
    if (!window.confirm(`Confermi di ${label} i privilegi di admin a ${target}?`)) return;
    setBusy(u.user_id);
    try {
      await api.gmSetAdmin(u.user_id, promote);
      toast.success(promote ? "Admin concesso" : "Admin revocato");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione fallita");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-3" data-testid="gm-admin-roles">
      <div className="flex items-center gap-2">
        <div>
          <div className="font-heading text-sm tracking-[0.3em] uppercase text-[#E5C34A] flex items-center gap-2">
            <Shield className="w-4 h-4" /> Gestione Admin
          </div>
          <div className="font-mono text-[9px] tracking-widest uppercase text-[#666]">
            Assegna o revoca privilegi. Whitelist (ENV) protetta.
          </div>
        </div>
        <button onClick={refresh} data-testid="roles-refresh"
          className="ml-auto px-3 py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase flex items-center gap-1.5">
          <RefreshCw className="w-3 h-3" /> Ricarica
        </button>
      </div>

      <div className="flex items-center gap-2 panel p-2">
        <Search className="w-3 h-3 text-[#666]" />
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder="Cerca per nome/email..."
          data-testid="roles-search"
          className="flex-1 bg-transparent border-none text-xs text-[#E2DFD8] font-mono outline-none" />
      </div>

      {!users && <div className="text-center text-xs italic text-[#666] py-6">Caricamento…</div>}
      <div className="space-y-1">
        {filtered.map(u => {
          const isAdmin = !!u.character?.is_admin;
          const isSelf = u.is_self;
          return (
            <div key={u.user_id}
              data-testid={`role-row-${u.user_id}`}
              className="panel-inner p-2 border border-[#262626] flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-heading text-[11px] text-[#E5C34A] truncate">
                  {u.character?.name || u.name || "?"}
                  {isAdmin && (
                    <span className="ml-2 px-1 border border-[#B89C3A] text-[#E5C34A] font-mono text-[8px]"
                      data-testid={`role-badge-${u.user_id}`}>ADMIN</span>
                  )}
                </div>
                <div className="font-mono text-[9px] text-[#666] truncate">{u.email}</div>
                <div className="font-mono text-[9px] text-[#B0ADA5]">
                  {u.character?.chosen_class || "—"} · Lv {u.character?.level || 1}
                </div>
              </div>
              {isSelf ? (
                <span className="font-mono text-[8px] uppercase text-[#6E8B3D] px-2">Sei tu</span>
              ) : isAdmin ? (
                <button onClick={() => setRole(u, false)} disabled={busy === u.user_id}
                  data-testid={`role-revoke-${u.user_id}`}
                  className="px-3 py-1.5 border border-[#8A0303] text-[#E33333] font-heading text-[10px] tracking-widest uppercase flex items-center gap-1 hover:bg-[#8A0303]/10 disabled:opacity-40">
                  <ShieldOff className="w-3 h-3" /> Revoca
                </button>
              ) : (
                <button onClick={() => setRole(u, true)} disabled={busy === u.user_id}
                  data-testid={`role-grant-${u.user_id}`}
                  className="px-3 py-1.5 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase flex items-center gap-1 hover:bg-[#4A5D23]/10 disabled:opacity-40">
                  <Shield className="w-3 h-3" /> Concedi
                </button>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && users && (
          <div className="text-center text-xs italic text-[#666] py-6">Nessun utente trovato.</div>
        )}
      </div>
    </div>
  );
}
